import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichTextarea } from "@/components/ui/RichTextarea";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, ArrowDown, Eye, EyeOff, Save, Type, Image as ImageIcon, Layout as LayoutIcon, RefreshCw, CheckCircle2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getCmsLayout, saveCmsLayoutDraft, publishCmsLayout, CmsLayout, CmsSectionConfig } from "@/data/cmsStore";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Megaphone } from "lucide-react";

import HeroSection from "@/components/HeroSection";
import HeroMiniCards from "@/components/HeroMiniCards";
import ElectoralMap from "@/components/ElectoralMap";
import PartiesSection from "@/components/PartiesSection";
import CTASection from "@/components/CTASection";
import AccreditationBarometers from "@/components/AccreditationBarometers";
import CitizenExpectations from "@/components/CitizenExpectations";
import PartiesEnActionSection from "@/components/PartiesEnActionSection";
import MediaDebatesSection from "@/components/MediaDebatesSection";
import QuizBanner from "@/components/QuizBanner";
import WhyParticipateSection from "@/components/WhyParticipateSection";
import ActionsCitoyennesSection from "@/components/ActionsCitoyennesSection";
import ResourcesSection from "@/components/ResourcesSection";

// Définition des champs modifiables par type de composant
const SECTION_SCHEMAS: Record<string, { key: string, label?: string, type: 'text' | 'textarea' | 'image' | 'boolean' | 'minicards_grid' | 'dynamic_timeline' | 'dynamic_faq' }[]> = {
  "HeroSection": [
    { key: "hero_title_fr", label: "Titre Principal (Fr)", type: "text" },
    { key: "hero_title_ar", label: "Titre Principal (Ar)", type: "text" },
    { key: "hero_subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" },
    { key: "hero_subtitle_ar", label: "Sous-titre (Ar)", type: "textarea" },
    { key: "hero_bg_image", label: "Image de fond (URL)", type: "image" }
  ],
  "HeroMiniCardsSection": [
    { key: "minicards", type: "minicards_grid" }
  ],
  "PartiesSection": [
    { key: "parties_title_fr", label: "Titre (Fr)", type: "text" },
    { key: "parties_desc_fr", label: "Description (Fr)", type: "textarea" },
    { key: "parties_title_ar", label: "Titre (Ar)", type: "text" },
    { key: "parties_desc_ar", label: "Description (Ar)", type: "textarea" },
    { key: "parties_title_zgh", label: "Titre (Zgh)", type: "text" },
    { key: "parties_desc_zgh", label: "Description (Zgh)", type: "textarea" }
  ],
  "CTASection": [
    { key: "cta_title_fr", label: "Titre CTA (Fr)", type: "text" },
    { key: "cta_btn_fr", label: "Bouton CTA (Fr)", type: "text" },
    { key: "cta_title_ar", label: "Titre CTA (Ar)", type: "text" },
    { key: "cta_btn_ar", label: "Bouton CTA (Ar)", type: "text" },
    { key: "cta_title_zgh", label: "Titre CTA (Zgh)", type: "text" },
    { key: "cta_btn_zgh", label: "Bouton CTA (Zgh)", type: "text" }
  ],
  "AccreditationBarometers": [
    { key: "title_fr", label: "Titre (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" },
    { key: "title_ar", label: "Titre (Ar)", type: "text" },
    { key: "subtitle_ar", label: "Sous-titre (Ar)", type: "textarea" },
    { key: "title_zgh", label: "Titre (Zgh)", type: "text" },
    { key: "subtitle_zgh", label: "Sous-titre (Zgh)", type: "textarea" }
  ],
  "CitizenExpectations": [
    { key: "title_fr", label: "Titre (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" },
    { key: "title_ar", label: "Titre (Ar)", type: "text" },
    { key: "subtitle_ar", label: "Sous-titre (Ar)", type: "textarea" },
    { key: "title_zgh", label: "Titre (Zgh)", type: "text" },
    { key: "subtitle_zgh", label: "Sous-titre (Zgh)", type: "textarea" }
  ],
  "PartiesEnActionSection": [
    { key: "title_fr", label: "Titre (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" },
    { key: "title_ar", label: "Titre (Ar)", type: "text" },
    { key: "subtitle_ar", label: "Sous-titre (Ar)", type: "textarea" },
    { key: "title_zgh", label: "Titre (Zgh)", type: "text" },
    { key: "subtitle_zgh", label: "Sous-titre (Zgh)", type: "textarea" }
  ],
  "MediaDebatesSection": [
    { key: "title_fr", label: "Titre (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" },
    { key: "title_ar", label: "Titre (Ar)", type: "text" },
    { key: "subtitle_ar", label: "Sous-titre (Ar)", type: "textarea" },
    { key: "title_zgh", label: "Titre (Zgh)", type: "text" },
    { key: "subtitle_zgh", label: "Sous-titre (Zgh)", type: "textarea" }
  ],
  "QuizBanner": [
    { key: "title_fr", label: "Titre (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Description (Fr)", type: "textarea" },
    { key: "btn_fr", label: "Bouton (Fr)", type: "text" }
  ],
  "WhyParticipateSection": [
    { key: "title_fr", label: "Titre (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" },
    { key: "title_ar", label: "Titre (Ar)", type: "text" },
    { key: "subtitle_ar", label: "Sous-titre (Ar)", type: "textarea" },
    { key: "title_zgh", label: "Titre (Zgh)", type: "text" },
    { key: "subtitle_zgh", label: "Sous-titre (Zgh)", type: "textarea" }
  ],
  "ActionsCitoyennesSection": [
    { key: "title_fr", label: "Titre (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" },
    { key: "title_ar", label: "Titre (Ar)", type: "text" },
    { key: "subtitle_ar", label: "Sous-titre (Ar)", type: "textarea" },
    { key: "title_zgh", label: "Titre (Zgh)", type: "text" },
    { key: "subtitle_zgh", label: "Sous-titre (Zgh)", type: "textarea" }
  ],
  "ResourcesSection": [
    { key: "title_fr", label: "Titre (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" },
    { key: "title_ar", label: "Titre (Ar)", type: "text" },
    { key: "subtitle_ar", label: "Sous-titre (Ar)", type: "textarea" },
    { key: "title_zgh", label: "Titre (Zgh)", type: "text" },
    { key: "subtitle_zgh", label: "Sous-titre (Zgh)", type: "textarea" }
  ],
  "CustomRichSection": [
    { key: "html_fr", label: "Contenu Libre (Fr)", type: "textarea" },
    { key: "html_ar", label: "Contenu Libre (Ar)", type: "textarea" },
    { key: "bg_color", label: "Couleur de fond (ex: bg-muted)", type: "text" },
    { key: "padding_y", label: "Espacement vertical (ex: py-12 ou py-20)", type: "text" }
  ],
  "CandidacyHeroSection": [
    { key: "title_fr", label: "Titre principal (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" }
  ],
  "CandidacyTabsSection": [
    { key: "general_eligibility_fr", label: "Éligibilité (Fr)", type: "textarea" },
    { key: "youth_desc_fr", label: "Description Jeunes (Fr)", type: "textarea" }
  ],
  "CandidacyRestrictionsSection": [
    { key: "incomp_desc_fr", label: "Description Incompatibilités (Fr)", type: "textarea" }
  ],
  "CandidacyCTASection": [],
  "ObserverHeroSection": [
    { key: "heroDesc_fr", label: "Description Hero (Fr)", type: "textarea" }
  ],
  "ObserverTabsSection": [
    { key: "definitionDesc_fr", label: "Définition de l'observateur (Fr)", type: "textarea" }
  ],
  "ObserverCTASection": [],
  "VoterHeroSection": [
    { key: "title_fr", label: "Titre principal (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" }
  ],
  "VoterVerificationSection": [
    { key: "check_desc_fr", label: "Texte Portail (Fr)", type: "textarea" },
    { key: "sms_desc_fr", label: "Texte SMS (Fr)", type: "textarea" }
  ],
  "VoterTimelineSection": [
    { key: "steps", type: "dynamic_timeline" }
  ],
  "VoterFAQSection": [
    { key: "faqs", type: "dynamic_faq" }
  ],
  "PourquoiHeroSection": [
    { key: "title_fr", label: "Titre principal (Fr)", type: "text" },
    { key: "subtitle_fr", label: "Sous-titre (Fr)", type: "textarea" }
  ],
  "PourquoiReasonsSection": [
    { key: "reason1_desc_fr", label: "Raison 1 (Fr)", type: "textarea" },
    { key: "reason2_desc_fr", label: "Raison 2 (Fr)", type: "textarea" }
  ],
  "PourquoiCTASection": []
};

const SECTION_NAMES: Record<string, string> = {
  "hero": "Héro (Bannière Principale)",
  "minicards": "Cartes Interactives (Match, Ring, Quiz...)",
  "accreditation": "Baromètres d'Accréditation",
  "map": "Carte Électorale Interactive",
  "expectations": "Attentes Citoyennes",
  "parties-action": "Partis en Action",
  "media": "Débats & Médias",
  "parties": "Annuaire des Partis",
  "quiz": "Bannière Quiz",
  "why": "Pourquoi Participer",
  "actions": "Actions Citoyennes",
  "resources": "Ressources & Guides",
  "cta": "Appel à l'Action",
  "institutions": "Institutions Constitutionnelles",
  "custom": "Section Sur-Mesure",
  "candidacy_hero": "En-tête (Hero)",
  "candidacy_tabs": "Onglets & Parcours",
  "candidacy_restrictions": "Incompatibilités",
  "candidacy_cta": "Appel à l'action final",
  "observateur_hero": "En-tête (Hero)",
  "observateur_tabs": "Onglets & Démarches",
  "observateur_cta": "Appel à l'action final",
  "voter_hero": "En-tête (Hero)",
  "voter_verification": "Vérification Inscription",
  "voter_timeline": "Parcours & Étapes",
  "voter_faq": "Foire Aux Questions",
  "pourquoi_hero": "En-tête (Hero)",
  "pourquoi_reasons": "Raisons de participer",
  "pourquoi_cta": "Appel à l'action final"
};

const AdminContentPage = () => {
  const [activePage, setActivePage] = useState<string>("home");
  const [layout, setLayout] = useState<CmsLayout>([]);
  const [activeTab, setActiveTab] = useState<"structure" | "content">("structure");
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  useEffect(() => {
    setLayout(getCmsLayout(activePage, true));
    setEditingSectionId(null);
  }, [activePage]);

  const handleSaveDraft = (newLayout: CmsLayout) => {
    setLayout(newLayout);
    saveCmsLayoutDraft(activePage, newLayout);
    toast.success("Brouillon sauvegardé !");
  };

  const moveSection = (index: number, direction: -1 | 1) => {
    if (index + direction < 0 || index + direction >= layout.length) return;
    const newLayout = [...layout];
    const temp = newLayout[index];
    newLayout[index] = newLayout[index + direction];
    newLayout[index + direction] = temp;
    handleSaveDraft(newLayout);
  };

  const addCustomSection = () => {
    const id = `custom_${Date.now()}`;
    const newLayout = [...layout, { id, type: "CustomRichSection", visible: true, content: {} }];
    SECTION_NAMES[id] = "Nouvelle Section Sur-Mesure";
    handleSaveDraft(newLayout);
    setActiveTab("content");
    setEditingSectionId(id);
  };

  const deleteCustomSection = (id: string) => {
    if (!window.confirm("Supprimer cette section sur-mesure ?")) return;
    const newLayout = layout.filter(s => s.id !== id);
    handleSaveDraft(newLayout);
    if (editingSectionId === id) setEditingSectionId(null);
  };

  const toggleVisibility = (id: string, visible: boolean) => {
    const newLayout = layout.map(s => s.id === id ? { ...s, visible } : s);
    handleSaveDraft(newLayout);
  };

  const updateContent = (sectionId: string, key: string, value: string) => {
    const newLayout = layout.map(s => {
      if (s.id === sectionId) {
        return { ...s, content: { ...s.content, [key]: value } };
      }
      return s;
    });
    setLayout(newLayout); // Update local state
    saveCmsLayoutDraft(activePage, newLayout); // Trigger instant live preview
  };

  const saveContentChanges = () => {
    saveCmsLayoutDraft(activePage, layout);
    toast.success("Brouillon sauvegardé !");
  };

  const publishChanges = () => {
    publishCmsLayout(activePage, layout);
    toast.success("Contenu publié avec succès !");
  };

  const resetCMS = () => {
    if (window.confirm("Êtes-vous sûr de vouloir réinitialiser toute la structure de cette page ?")) {
      localStorage.removeItem(`kaynin_cms_layout_${activePage}`);
      localStorage.removeItem(`kaynin_cms_layout_draft_${activePage}`);
      setLayout(getCmsLayout(activePage));
      toast.success("CMS réinitialisé.");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Gestion de Contenu (CMS)</h1>
          <p className="text-muted-foreground font-body">Personnalisez manuellement l'architecture de vos pages.</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={activePage} onValueChange={setActivePage}>
            <SelectTrigger className="w-[250px] font-bold">
              <SelectValue placeholder="Choisir la page à modifier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="home">🏠 Page d'Accueil</SelectItem>
              <SelectItem value="candidature">👔 Je veux être candidat</SelectItem>
              <SelectItem value="observateur">👁️ Je veux être observateur</SelectItem>
              <SelectItem value="voter">🗳️ Je veux voter</SelectItem>
              <SelectItem value="pourquoi">🤔 Pourquoi participer ?</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div className="flex bg-muted p-1 rounded-xl w-fit">
          <Button 
            variant={activeTab === "structure" ? "default" : "ghost"} 
            size="sm" 
            className="rounded-lg gap-2"
            onClick={() => setActiveTab("structure")}
          >
            <LayoutIcon className="w-4 h-4" /> Architecture
          </Button>
          <Button 
            variant={activeTab === "content" ? "default" : "ghost"} 
            size="sm" 
            className="rounded-lg gap-2"
            onClick={() => setActiveTab("content")}
          >
            <Type className="w-4 h-4" /> Textes & Contenu
          </Button>
        </div>
        <div className="hidden md:block">
           <Button asChild className="bg-orange-500 hover:bg-orange-600 text-white gap-2 shadow-sm rounded-xl">
             <Link to="/dashboard/spot-news">
               <Megaphone className="w-4 h-4" /> Gérer les Spot News
             </Link>
           </Button>
        </div>
      </div>
      
      <div className="md:hidden">
         <Button asChild className="w-full bg-orange-500 hover:bg-orange-600 text-white gap-2 shadow-sm rounded-xl">
           <Link to="/dashboard/spot-news">
             <Megaphone className="w-4 h-4" /> Gérer les Spot News
           </Link>
         </Button>
      </div>

      <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm p-4 rounded-xl flex items-start gap-3">
        <div className="mt-0.5">ℹ️</div>
        <div>
          <strong>Note de fonctionnement :</strong> Les modifications effectuées ici sont répercutées instantanément sur le site public. La page adaptera son affichage selon l'ordre défini ci-dessous.
        </div>
      </div>

      {activeTab === "structure" && (
        <Card className="border-border/50 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Structure de la Page d'Accueil</CardTitle>
              <CardDescription>Glissez ou utilisez les flèches pour changer l'ordre. Masquez les sections non désirées.</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={resetCMS} className="text-destructive border-destructive/20 hover:bg-destructive/10">
              <RefreshCw className="w-4 h-4 mr-2" /> Réinitialiser
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {layout.map((section, index) => (
              <div 
                key={section.id} 
                className={`flex items-center justify-between p-4 rounded-xl border transition-colors ${section.visible ? 'bg-card border-border' : 'bg-muted/30 border-dashed border-border/50 opacity-60'}`}
              >
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-1">
                    <Button 
                      variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:bg-muted" 
                      onClick={() => moveSection(index, -1)} disabled={index === 0}
                    >
                      <ArrowUp className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:bg-muted" 
                      onClick={() => moveSection(index, 1)} disabled={index === layout.length - 1}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </Button>
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground flex items-center gap-2">
                      {SECTION_NAMES[section.id] || section.type}
                      {!section.visible && <Badge variant="secondary" className="text-xs">Masqué</Badge>}
                    </h3>
                    <p className="text-xs text-muted-foreground font-mono">{section.type}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-muted-foreground hidden sm:inline-block">
                    {section.visible ? "Visible" : "Caché"}
                  </span>
                  <Switch 
                    checked={section.visible} 
                    onCheckedChange={(c) => toggleVisibility(section.id, c)} 
                  />
                  {section.type === "CustomRichSection" && (
                    <Button variant="ghost" size="sm" className="text-destructive h-8" onClick={() => deleteCustomSection(section.id)}>
                      Supprimer
                    </Button>
                  )}
                </div>
              </div>
            ))}
            
            <Button onClick={addCustomSection} variant="outline" className="w-full mt-4 border-dashed border-2 hover:bg-muted/50">
              + Ajouter un espace d'écriture sur-mesure
            </Button>
          </CardContent>
        </Card>
      )}

      {activeTab === "content" && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1 space-y-2">
            <h3 className="font-semibold text-lg px-2">Sélectionner une section</h3>
            <div className="flex flex-col gap-1">
              {layout.map(section => {
                const hasSchema = !!SECTION_SCHEMAS[section.type];
                return (
                  <button
                    key={section.id}
                    onClick={() => setEditingSectionId(section.id)}
                    className={`text-left px-4 py-3 rounded-xl text-sm font-semibold transition-colors flex justify-between items-center ${
                      editingSectionId === section.id ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-muted text-muted-foreground"
                    }`}
                  >
                    <span>{SECTION_NAMES[section.id] || section.type}</span>
                    {!hasSchema && <span className="text-[10px] uppercase opacity-60">Non éditable</span>}
                    {hasSchema && editingSectionId !== section.id && <Type className="w-4 h-4 opacity-50" />}
                  </button>
                );
              })}
            </div>
          </div>
          
          <div className="md:col-span-2">
            <Card className="min-h-[400px]">
              <CardHeader className="flex flex-row items-center justify-between border-b pb-4">
                <div>
                  <CardTitle className="text-xl">
                    Édition : {editingSectionId ? SECTION_NAMES[editingSectionId] || editingSectionId : "Aucune section"}
                  </CardTitle>
                  <CardDescription>Personnalisez les textes de cette section.</CardDescription>
                </div>
                {editingSectionId && SECTION_SCHEMAS[layout.find(s => s.id === editingSectionId)?.type || ""] && (
                  <div className="flex gap-3">
                    <Button onClick={saveContentChanges} variant="outline" className="gap-2">
                      <Save className="w-4 h-4" /> Enregistrer Brouillon
                    </Button>
                    <Button onClick={publishChanges} className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white shadow-md">
                      <CheckCircle2 className="w-4 h-4" /> Publier
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="pt-6">
                {!editingSectionId ? (
                  <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                    <Type className="w-12 h-12 mb-4 opacity-20" />
                    <p>Sélectionnez une section à gauche pour modifier son contenu.</p>
                  </div>
                ) : (() => {
                  const section = layout.find(s => s.id === editingSectionId);
                  if (!section) return null;
                  const schema = SECTION_SCHEMAS[section.type];
                  
                  if (!schema) {
                    return (
                      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground text-center px-4">
                        <LayoutIcon className="w-12 h-12 mb-4 opacity-20" />
                        <p className="font-semibold">Options d'édition non disponibles</p>
                        <p className="text-sm mt-2">Cette section est gérée dynamiquement par d'autres modules du tableau de bord (ex: Candidats, Événements) ou ne contient pas de texte statique personnalisable.</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-6">
                      {schema.map(field => {
                        if (field.type === 'minicards_grid') {
                          return (
                            <div key="minicards_grid" className="col-span-1 mt-2">
                              <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-4 gap-4">
                                {/* Carte Match */}
                                <div className="p-4 border rounded-xl bg-card space-y-4">
                                  <h5 className="font-semibold text-rose-600">Carte Match</h5>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Titre (Fr)</label>
                                    <Input value={section.content["minicard_match_title_fr"] || ""} onChange={(e) => updateContent(section.id, "minicard_match_title_fr", e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Sous-titre (Fr)</label>
                                    <Input value={section.content["minicard_match_subtitle_fr"] || ""} onChange={(e) => updateContent(section.id, "minicard_match_subtitle_fr", e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Lien URL</label>
                                    <Input placeholder="/match" value={section.content["minicard_match_link"] || ""} onChange={(e) => updateContent(section.id, "minicard_match_link", e.target.value)} />
                                  </div>
                                </div>
                                {/* Carte Ring */}
                                <div className="p-4 border rounded-xl bg-card space-y-4">
                                  <h5 className="font-semibold text-red-600">Carte Ring</h5>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Titre (Fr)</label>
                                    <Input value={section.content["minicard_ring_title_fr"] || ""} onChange={(e) => updateContent(section.id, "minicard_ring_title_fr", e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Sous-titre (Fr)</label>
                                    <Input value={section.content["minicard_ring_subtitle_fr"] || ""} onChange={(e) => updateContent(section.id, "minicard_ring_subtitle_fr", e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Lien URL</label>
                                    <Input placeholder="/ring" value={section.content["minicard_ring_link"] || ""} onChange={(e) => updateContent(section.id, "minicard_ring_link", e.target.value)} />
                                  </div>
                                </div>
                                {/* Carte Quiz */}
                                <div className="p-4 border rounded-xl bg-card space-y-4">
                                  <h5 className="font-semibold text-orange-500">Carte Quiz</h5>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Titre (Fr)</label>
                                    <Input value={section.content["minicard_quiz_title_fr"] || ""} onChange={(e) => updateContent(section.id, "minicard_quiz_title_fr", e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Sous-titre (Fr)</label>
                                    <Input value={section.content["minicard_quiz_subtitle_fr"] || ""} onChange={(e) => updateContent(section.id, "minicard_quiz_subtitle_fr", e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Lien URL</label>
                                    <Input placeholder="/quiz" value={section.content["minicard_quiz_link"] || ""} onChange={(e) => updateContent(section.id, "minicard_quiz_link", e.target.value)} />
                                  </div>
                                </div>
                                {/* Carte Kaynin Local */}
                                <div className="p-4 border rounded-xl bg-card space-y-4">
                                  <h5 className="font-semibold text-emerald-600">Carte Kayn'in Local</h5>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Titre (Fr)</label>
                                    <Input value={section.content["minicard_djellaba_title_fr"] || ""} onChange={(e) => updateContent(section.id, "minicard_djellaba_title_fr", e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Sous-titre (Fr)</label>
                                    <Input value={section.content["minicard_djellaba_subtitle_fr"] || ""} onChange={(e) => updateContent(section.id, "minicard_djellaba_subtitle_fr", e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Lien URL</label>
                                    <Input placeholder="Lien ou laissé vide" value={section.content["minicard_djellaba_link"] || ""} onChange={(e) => updateContent(section.id, "minicard_djellaba_link", e.target.value)} />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">Message Popup (si lien vide)</label>
                                    <Textarea rows={4} value={section.content["minicard_djellaba_message_fr"] || ""} onChange={(e) => updateContent(section.id, "minicard_djellaba_message_fr", e.target.value)} />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        }

                        if (field.type === 'dynamic_timeline' || field.type === 'dynamic_faq') {
                          const isTimeline = field.type === 'dynamic_timeline';
                          let items: any[] = [];
                          try {
                            items = JSON.parse(section.content[field.key] || "[]");
                          } catch (e) { items = []; }
                        
                          return (
                            <div key={field.key} className="col-span-1 mt-2 space-y-4 p-4 border rounded-xl bg-card">
                              <div className="flex justify-between items-center">
                                <h5 className="font-semibold text-primary">{isTimeline ? "Étapes du parcours" : "Questions de la FAQ"}</h5>
                                <Button size="sm" variant="outline" onClick={() => {
                                  const newItem = isTimeline 
                                    ? { id: `step_${Date.now()}`, title_fr: "Nouvelle étape", desc_fr: "" }
                                    : { id: `faq_${Date.now()}`, question_fr: "Nouvelle question ?", answer_fr: "" };
                                  const newItems = [...items, newItem];
                                  updateContent(section.id, field.key, JSON.stringify(newItems));
                                }}>
                                  + Ajouter {isTimeline ? "une étape" : "une question"}
                                </Button>
                              </div>
                              
                              {items.map((item, idx) => (
                                <div key={item.id} className="p-4 border rounded-lg bg-muted/30 space-y-4 relative">
                                  <Button 
                                    size="sm" 
                                    variant="destructive" 
                                    className="absolute top-2 right-2 h-8 text-xs"
                                    onClick={() => {
                                      if(!window.confirm("Supprimer cet élément ?")) return;
                                      const newItems = items.filter(i => i.id !== item.id);
                                      updateContent(section.id, field.key, JSON.stringify(newItems));
                                    }}
                                  >
                                    Supprimer
                                  </Button>
                                  
                                  <div className="flex gap-2 mb-2">
                                    <Button size="icon" variant="outline" className="h-8 w-8" disabled={idx === 0} onClick={() => {
                                      const newItems = [...items];
                                      const temp = newItems[idx];
                                      newItems[idx] = newItems[idx-1];
                                      newItems[idx-1] = temp;
                                      updateContent(section.id, field.key, JSON.stringify(newItems));
                                    }}><ArrowUp className="w-4 h-4"/></Button>
                                    <Button size="icon" variant="outline" className="h-8 w-8" disabled={idx === items.length - 1} onClick={() => {
                                      const newItems = [...items];
                                      const temp = newItems[idx];
                                      newItems[idx] = newItems[idx+1];
                                      newItems[idx+1] = temp;
                                      updateContent(section.id, field.key, JSON.stringify(newItems));
                                    }}><ArrowDown className="w-4 h-4"/></Button>
                                  </div>
                        
                                  <div className="space-y-2 pr-20">
                                    <label className="text-xs font-semibold">{isTimeline ? "Titre (Fr)" : "Question (Fr)"}</label>
                                    <Input 
                                      value={isTimeline ? item.title_fr : item.question_fr} 
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        if (isTimeline) newItems[idx].title_fr = e.target.value;
                                        else newItems[idx].question_fr = e.target.value;
                                        updateContent(section.id, field.key, JSON.stringify(newItems));
                                      }} 
                                    />
                                  </div>
                                  <div className="space-y-2">
                                    <label className="text-xs font-semibold">{isTimeline ? "Description (Fr)" : "Réponse (Fr)"}</label>
                                    <RichTextarea 
                                      value={isTimeline ? item.desc_fr : item.answer_fr} 
                                      onChange={(e) => {
                                        const newItems = [...items];
                                        if (isTimeline) newItems[idx].desc_fr = e.target.value;
                                        else newItems[idx].answer_fr = e.target.value;
                                        updateContent(section.id, field.key, JSON.stringify(newItems));
                                      }} 
                                      placeholder=""
                                      rows={3}
                                      className="font-body text-sm"
                                    />
                                  </div>
                                  
                                  <details className="text-sm">
                                    <summary className="text-muted-foreground cursor-pointer mt-2 text-xs font-semibold bg-muted inline-block px-3 py-1 rounded-full">Traductions (Arabe & Amazigh)</summary>
                                    <div className="grid grid-cols-2 gap-4 mt-3 p-3 bg-background rounded-xl border">
                                      <div className="space-y-3">
                                        <div className="space-y-1">
                                          <label className="text-xs font-semibold">{isTimeline ? "Titre (Ar)" : "Question (Ar)"}</label>
                                          <Input dir="rtl" value={isTimeline ? (item.title_ar||'') : (item.question_ar||'')} onChange={(e) => {
                                            const newItems = [...items];
                                            if (isTimeline) newItems[idx].title_ar = e.target.value; else newItems[idx].question_ar = e.target.value;
                                            updateContent(section.id, field.key, JSON.stringify(newItems));
                                          }} />
                                        </div>
                                        <div className="space-y-1">
                                          <label className="text-xs font-semibold">{isTimeline ? "Desc (Ar)" : "Réponse (Ar)"}</label>
                                          <Textarea dir="rtl" rows={4} value={isTimeline ? (item.desc_ar||'') : (item.answer_ar||'')} onChange={(e) => {
                                            const newItems = [...items];
                                            if (isTimeline) newItems[idx].desc_ar = e.target.value; else newItems[idx].answer_ar = e.target.value;
                                            updateContent(section.id, field.key, JSON.stringify(newItems));
                                          }} />
                                        </div>
                                      </div>
                                      <div className="space-y-3">
                                        <div className="space-y-1">
                                          <label className="text-xs font-semibold">{isTimeline ? "Titre (Zgh)" : "Question (Zgh)"}</label>
                                          <Input value={isTimeline ? (item.title_zgh||'') : (item.question_zgh||'')} onChange={(e) => {
                                            const newItems = [...items];
                                            if (isTimeline) newItems[idx].title_zgh = e.target.value; else newItems[idx].question_zgh = e.target.value;
                                            updateContent(section.id, field.key, JSON.stringify(newItems));
                                          }} />
                                        </div>
                                        <div className="space-y-1">
                                          <label className="text-xs font-semibold">{isTimeline ? "Desc (Zgh)" : "Réponse (Zgh)"}</label>
                                          <Textarea rows={4} value={isTimeline ? (item.desc_zgh||'') : (item.answer_zgh||'')} onChange={(e) => {
                                            const newItems = [...items];
                                            if (isTimeline) newItems[idx].desc_zgh = e.target.value; else newItems[idx].answer_zgh = e.target.value;
                                            updateContent(section.id, field.key, JSON.stringify(newItems));
                                          }} />
                                        </div>
                                      </div>
                                    </div>
                                  </details>
                                </div>
                              ))}
                            </div>
                          );
                        }

                        const isBoolean = field.type === 'boolean';
                        // if boolean, default is false unless explicitly set to 'true' string or boolean true
                        const val = section.content[field.key];
                        const boolValue = val === 'true' || val === true;

                        return (
                          <div key={field.key} className={isBoolean ? "flex items-center justify-between p-4 border rounded-xl bg-card" : "space-y-2"}>
                            <div className={isBoolean ? "flex flex-col" : "flex flex-col"}>
                              <label className="text-sm font-semibold flex items-center gap-2">
                                {field.type === 'image' && <ImageIcon className="w-4 h-4 text-muted-foreground"/>}
                                {field.type === 'text' && <Type className="w-4 h-4 text-muted-foreground"/>}
                                {field.type === 'textarea' && <Type className="w-4 h-4 text-muted-foreground"/>}
                                {field.label}
                              </label>
                            </div>
                            {field.type === 'textarea' ? (
                              <RichTextarea 
                                value={section.content[field.key] || ""} 
                                onChange={(e) => updateContent(section.id, field.key, e.target.value)}
                                placeholder={`Texte par défaut de la plateforme...`}
                                rows={6}
                                className="font-body text-sm"
                              />
                            ) : field.type === 'boolean' ? (
                              <Switch 
                                checked={boolValue} 
                                onCheckedChange={(c) => updateContent(section.id, field.key, c ? 'true' : 'false')}
                              />
                            ) : (
                              <Input 
                                type={field.type === 'image' ? 'url' : 'text'}
                                value={section.content[field.key] || ""} 
                                onChange={(e) => updateContent(section.id, field.key, e.target.value)}
                                placeholder={field.type === 'image' ? "https://..." : `Contenu par défaut...`}
                                className="rounded-xl focus:ring-primary/30"
                                dir={field.label && field.label.includes("Arabe") ? "rtl" : "ltr"}
                              />
                            )}
                            {!isBoolean && <p className="text-[10px] text-muted-foreground">Laissez vide pour utiliser le texte/image par défaut de Kayn'in.</p>}
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Rendering the Preview Component */}
            {editingSectionId && (() => {
              const section = layout.find(s => s.id === editingSectionId);
              if (!section) return null;
              
              let PreviewComponent = null;
              if (section.type === "HeroSection") PreviewComponent = <HeroSection />;
              if (section.type === "HeroMiniCardsSection") PreviewComponent = <div className="p-8 bg-black/5 rounded-xl"><HeroMiniCards /></div>;
              if (section.type === "ElectoralMap") PreviewComponent = <ElectoralMap />;
              if (section.type === "PartiesSection") PreviewComponent = <div className="py-8"><PartiesSection /></div>;
              if (section.type === "CTASection") PreviewComponent = <CTASection />;
              if (section.type === "AccreditationBarometers") PreviewComponent = <AccreditationBarometers />;
              if (section.type === "CitizenExpectations") PreviewComponent = <CitizenExpectations />;
              if (section.type === "PartiesEnActionSection") PreviewComponent = <PartiesEnActionSection />;
              if (section.type === "MediaDebatesSection") PreviewComponent = <MediaDebatesSection />;
              if (section.type === "QuizBanner") PreviewComponent = <QuizBanner />;
              if (section.type === "WhyParticipateSection") PreviewComponent = <WhyParticipateSection />;
              if (section.type === "ActionsCitoyennesSection") PreviewComponent = <ActionsCitoyennesSection />;
              if (section.type === "ResourcesSection") PreviewComponent = <ResourcesSection />;
              if (section.type === "CustomRichSection") PreviewComponent = (
                <div className={`${section.content.bg_color || 'bg-background'} ${section.content.padding_y || 'py-12'}`}>
                  <div className="container mx-auto px-4">
                    <div dangerouslySetInnerHTML={{ __html: section.content.html_fr || '<i>Section vide...</i>' }} />
                  </div>
                </div>
              );

              // To avoid importing heavy candidate components in admin, we just show a placeholder
              if (section.type.includes("Candidacy") || section.type.includes("Observer") || section.type.includes("Voter") || section.type.includes("Pourquoi")) {
                PreviewComponent = (
                  <div className="py-12 px-6 text-center border-dashed border-2 rounded-xl bg-muted/20">
                    <h3 className="font-bold text-xl">{SECTION_NAMES[section.id] || section.type}</h3>
                    <p className="text-muted-foreground text-sm mt-2">Ce module est complexe. Modifiez ses textes via le formulaire ci-dessus, et vérifiez le rendu sur la page publique.</p>
                  </div>
                );
              }
              
              if (PreviewComponent) {
                return (
                  <div className="mt-8 border-2 border-primary/20 rounded-xl overflow-hidden shadow-sm relative">
                    <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-xl z-20 shadow-md">
                      Aperçu en direct (Brouillon)
                    </div>
                    {/* The pointer-events-none makes the preview non-interactive to not break the admin page */}
                    <div className="pointer-events-none" style={{ zoom: 0.8 }}>
                      {PreviewComponent}
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminContentPage;
